/**
 * Decisions for a Stripe payment the applicant already started, as plain
 * functions.
 *
 * Kept outside `payment.tsx` so they can be tested: the step is a React
 * component in a package with no jsdom and no testing-library. Nothing here
 * touches the DOM or React; the caller passes in the save and the Stripe
 * lookup.
 */
import { PaymentIntent, PaymentIntentResult } from "@stripe/stripe-js";
import { ConditionalNextSlug } from "@dreambigger/shared/src/types/acquire";
import { changeStepForward } from "../utils/changeStepForward";

/**
 * Statuses that always mean the applicant submitted the payment: charged
 * (`succeeded`), authorized and held for capture (`requires_capture`), or sent
 * by a bank debit that can take days to succeed or fail (`processing`).
 */
const SUBMITTED_PAYMENT_INTENT_STATUSES: PaymentIntent.Status[] = [
  "succeeded",
  "requires_capture",
  "processing",
];

/**
 * Whether the applicant submitted this payment intent, so no second payment may
 * be taken for it. Besides the statuses above, `requires_action` counts while a
 * bank account waits for microdeposit verification: the applicant finishes that
 * from Stripe's email, not on our page, and the debit follows. Any other
 * `requires_action` is authentication, such as 3D Secure, that the applicant did
 * not complete.
 *
 * Stricter than the statuses the payment step treats as submitted once saved:
 * a saved `requires_action` came from the payment status step after Stripe's
 * redirect, or from this check for microdeposits, while one read here may be
 * authentication the applicant abandoned.
 */
export const isSubmittedPaymentIntent = ({
  status,
  next_action: nextAction,
}: Pick<PaymentIntent, "status" | "next_action">): boolean =>
  SUBMITTED_PAYMENT_INTENT_STATUSES.includes(status) ||
  (status === "requires_action" &&
    nextAction?.type === "verify_with_microdeposits");

/**
 * Asks Stripe for the status of the payment intent behind a client secret.
 * Resolves to the status when the applicant submitted the payment, and to
 * undefined when they have not. Rejects when Stripe could not answer, so the
 * caller neither shows the form for a submitted payment nor creates a second
 * payment intent.
 */
export const getSubmittedPaymentIntentStatus = async ({
  clientSecret,
  retrievePaymentIntent,
}: {
  clientSecret: string;
  retrievePaymentIntent: (clientSecret: string) => Promise<PaymentIntentResult>;
}): Promise<PaymentIntent.Status | undefined> => {
  const result = await retrievePaymentIntent(clientSecret);

  if (result.error) {
    throw new Error(
      result.error.message ?? "Unable to retrieve the payment intent"
    );
  }

  const { paymentIntent } = result;

  // Not submitted: Stripe returned no payment intent, or one the applicant has
  // not paid (no payment yet, a failed or declined attempt, unfinished card
  // authentication, or canceled). The caller then shows the form again.
  if (!paymentIntent || !isSubmittedPaymentIntent(paymentIntent)) {
    return undefined;
  }

  return paymentIntent.status;
};

/**
 * Moves the applicant past a payment they already submitted. Saves the payment
 * response again first: passing through without a save reads as skipping the
 * step, so the forward-jump guard sends the applicant back, or the payment
 * response is archived at submit and a held payment is never captured. Stays on
 * the step when the save fails; the save already told the applicant.
 */
export const continuePastSubmittedPayment = async ({
  savePaymentResponse,
  nextSlug,
}: {
  savePaymentResponse: () => Promise<boolean>;
  nextSlug?: string | ConditionalNextSlug;
}): Promise<void> => {
  const saved = await savePaymentResponse();

  if (!saved) {
    return;
  }

  changeStepForward(nextSlug, {});
};
